/**
 * IntentListPanel — Zone 2 for `motivate` / `classroom` / `school` (spec §3).
 * Type-to-filter over the curated node set from GET /api/categories/intent-list.
 * Fixed-height internally-scrolling list, filter pinned on top (the prompt
 * below must never leave the viewport). Rows render `name — parent` context
 * (fixes duplicate labels like "Critical Thinking" ×3). `school` gets the
 * mascot lane: a dedicated type-ahead over mascot leaves with singular/plural
 * variants display-collapsed; the tree's useless intermediate levels
 * ("Warriors & Knights", "Other") never render anywhere.
 *
 * Every failure degrades invisibly: endpoint down → panel hides, the prompt
 * carries the poster and the worker's scoped matcher backfills the category.
 */
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import usePrintables from '../../../services/usePrintables';

export default function IntentListPanel({ intent, value, onChange }) {
  const { t } = useTranslation('home');
  const { fetchIntentList } = usePrintables();
  const [nodes, setNodes] = useState(null); // null = loading, [] = failed/empty
  const [q, setQ] = useState('');
  const [section, setSection] = useState('');

  useEffect(() => {
    let cancelled = false;
    setNodes(null);
    setQ('');
    setSection('');
    fetchIntentList(intent)
      .then((rows) => { if (!cancelled) setNodes(rows); })
      .catch(() => { if (!cancelled) setNodes([]); });
    return () => { cancelled = true; };
  }, [intent, fetchIntentList]);

  const isMascot = (n) => (n.breadcrumb || '').includes('Mascots') || n.parent_name === 'Mascots';

  // Mascot display-collapse: singular/plural + spacing variants share a
  // normalized key; the canonical (longest, usually plural) row displays.
  const { mascots, listNodes, sections } = useMemo(() => {
    if (!nodes) return { mascots: [], listNodes: [], sections: [] };
    const m = new Map();
    const rest = [];
    for (const n of nodes) {
      if (intent === 'school' && isMascot(n)) {
        if (n.is_branch) continue; // never show mascot tree levels
        const key = n.name.toLowerCase().replace(/[^a-z]/g, '').replace(/s$/, '');
        const cur = m.get(key);
        if (!cur || n.name.length > cur.name.length) m.set(key, n);
      } else if (!n.is_branch || n.parent_name === null) {
        // leaves + top-level branches (branches are legal pick targets —
        // 281 published posters live on branches), but skip deep branches
        // to keep the list scannable.
        rest.push(n);
      }
    }
    const secs = [...new Set(rest.map((n) => n.parent_name).filter(Boolean))].sort();
    return { mascots: [...m.values()].sort((a, b) => a.name.localeCompare(b.name)), listNodes: rest, sections: secs };
  }, [nodes, intent]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return listNodes.filter((n) =>
      (!section || n.parent_name === section) &&
      (!needle || n.name.toLowerCase().includes(needle) || (n.parent_name || '').toLowerCase().includes(needle)));
  }, [listNodes, q, section]);

  const mascotFiltered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return mascots.filter((n) => n.name.toLowerCase().includes(needle)).slice(0, 8);
  }, [mascots, q]);

  // ── Selected: chip ──
  if (value?.id) {
    return (
      <div style={{ fontFamily: 'inherit' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          fontSize: '0.8rem', fontWeight: 600, color: 'rgba(127,75,246,0.95)',
          background: 'rgba(127,75,246,0.14)', border: '1px solid rgba(127,75,246,0.35)',
          padding: '6px 10px', borderRadius: 8,
        }}>
          {value.isMascot
            ? t('intentList.chipMascot', { name: value.name })
            : t('intentList.chipCategory', { name: value.name })}
          <span
            onClick={() => onChange(null)}
            title={t('intentList.removeChip')}
            style={{ cursor: 'pointer', color: 'rgba(27,37,80,0.57)', fontWeight: 700 }}
          >
            ×
          </span>
        </span>
        {value.requiresBlankFields && (
          <div style={{ fontSize: '0.72rem', color: 'rgba(27,37,80,0.427)', marginTop: 6 }}>
            {t('intentList.blankFieldsNote')}
          </div>
        )}
      </div>
    );
  }

  if (nodes === null) {
    return <div style={{ fontFamily: 'inherit', fontSize: '0.78rem', color: 'rgba(27,37,80,0.332)' }}>{t('intentList.loading')}</div>;
  }
  if (nodes.length === 0) return null; // endpoint down/empty — degrade invisibly

  return (
    <div style={{ fontFamily: 'inherit' }}>
      <input
        type="text"
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={intent === 'school' ? t('intentList.mascotPlaceholder') : t('intentList.filterPlaceholder')}
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 10, boxSizing: 'border-box',
          border: '1px solid rgba(27,37,80,0.095)', background: '#FFFFFF',
          color: 'rgba(27,37,80,0.874)', fontSize: '0.85rem', outline: 'none',
          fontFamily: 'inherit', marginBottom: 8,
        }}
      />

      {intent === 'classroom' && sections.length > 1 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {sections.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSection(section === s ? '' : s)}
              style={{
                padding: '4px 10px', borderRadius: 999, fontSize: '0.7rem', fontFamily: 'inherit',
                border: section === s ? '1px solid rgba(127,75,246,0.5)' : '1px solid rgba(27,37,80,0.095)',
                background: section === s ? 'rgba(127,75,246,0.14)' : 'transparent',
                color: section === s ? 'rgba(127,75,246,0.95)' : 'rgba(27,37,80,0.522)',
                cursor: 'pointer',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Mascot lane (school): appears as soon as the filter text matches. */}
      {mascotFiltered.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 600, color: 'rgba(27,37,80,0.38)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            {t('intentList.mascotsHeading')}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {mascotFiltered.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => onChange({ id: n.id, name: n.name, breadcrumb: n.breadcrumb, isMascot: true })}
                style={{
                  padding: '6px 12px', borderRadius: 999, fontSize: '0.8rem', fontWeight: 600,
                  border: '1px solid rgba(27,37,80,0.133)', background: '#FFFFFF',
                  color: 'rgba(27,37,80,0.807)', cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {n.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Fixed-height internally-scrolling list — the prompt stays visible below.
          overscrollBehavior contain: edge scrolls must not chain into the page. */}
      <div style={{ maxHeight: 240, overflowY: 'auto', overscrollBehavior: 'contain', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {filtered.slice(0, 200).map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => onChange({
              id: n.id, name: n.name, breadcrumb: n.breadcrumb,
              requiresBlankFields: !!n.requires_blank_fields,
            })}
            style={{
              textAlign: 'left', padding: '8px 10px', borderRadius: 8,
              border: '1px solid rgba(27,37,80,0.076)', background: '#FFFFFF',
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'baseline', gap: 8, justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: '0.8rem', color: 'rgba(27,37,80,0.807)', fontWeight: 600 }}>
              {n.name}
              {n.parent_name && (
                <span style={{ fontWeight: 400, color: 'rgba(27,37,80,0.38)' }}> — {n.parent_name}</span>
              )}
            </span>
            {n.requires_blank_fields && (
              <span style={{
                fontSize: '0.62rem', color: 'rgba(26,176,242,0.9)', whiteSpace: 'nowrap',
                border: '1px solid rgba(26,176,242,0.35)', borderRadius: 999, padding: '1px 7px',
              }}>
                {t('intentList.blankFieldsBadge')}
              </span>
            )}
          </button>
        ))}
        {filtered.length === 0 && q.trim().length > 0 && mascotFiltered.length === 0 && (
          <div style={{ fontSize: '0.75rem', color: 'rgba(27,37,80,0.332)', padding: '4px 2px' }}>
            {t('intentList.noMatch')}
          </div>
        )}
      </div>
    </div>
  );
}
